import { useState } from "react";
import { useCancelLabSubscription } from "@/lib/api/labHooks";
import { Button } from "@/design-system/primitives";
import type { LabSubscription } from "@/types/labs";
import { labDate, labMoney } from "@/features/labs/labPresentation";
import { labIntervalLabel } from "@/features/labs/LabPurchaseOptions";

export function LabSubscriptionCards({
  subscriptions,
}: {
  subscriptions: LabSubscription[];
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const cancel = useCancelLabSubscription();
  if (!subscriptions.length) return null;
  return (
    <section
      aria-label="Blood test subscriptions"
      className="ph-no-capture flex flex-col gap-15"
    >
      {subscriptions.map((sub) => (
        <div key={sub.id} className="border border-line-subtle p-20">
          <div className="flex flex-wrap items-start justify-between gap-15">
            <div>
              <p className="type-label-xs text-ink-muted mb-8">Blood test</p>
              <h3 className="font-display text-20">{sub.product_name}</h3>
              <p className="type-body-md mt-10">
                {labMoney(sub.amount_cents, sub.currency)}{" "}
                {sub.cancel_at_period_end ||
                ["canceled", "incomplete_expired"].includes(sub.status)
                  ? "per kit"
                  : `every ${labIntervalLabel(sub.interval_months)}`}{" "}
                · One kit per delivery
              </p>
              <p className="type-body-sm text-ink-secondary mt-8">
                {["canceled", "incomplete_expired"].includes(sub.status)
                  ? "Subscription ended"
                  : sub.cancel_at_period_end
                    ? "Future renewals cancelled. Your current kit and results remain available."
                    : ["past_due", "unpaid", "incomplete"].includes(sub.status)
                      ? "Payment needs attention. No new kit is sent until payment is confirmed."
                      : `Next charge and kit: ${labDate(sub.next_billing_at)}`}
              </p>
            </div>
            {!sub.cancel_at_period_end &&
              !["canceled", "incomplete_expired"].includes(sub.status) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    cancel.reset();
                    setConfirmId(sub.id);
                  }}
                >
                  Cancel future renewals
                </Button>
              )}
          </div>
          {confirmId === sub.id && (
            <div className="mt-15 border-t border-line-subtle pt-15">
              <p className="type-body-sm">
                Stop future charges and kits? Your current order and results
                remain available.
              </p>
              <div className="flex flex-wrap gap-10 mt-10">
                <Button
                  disabled={cancel.isPending}
                  onClick={() =>
                    cancel.mutate(sub.id, {
                      onSuccess: () => setConfirmId(null),
                    })
                  }
                >
                  Confirm cancellation
                </Button>
                <Button variant="secondary" onClick={() => setConfirmId(null)}>
                  Keep subscription
                </Button>
              </div>
              {cancel.isError && (
                <p role="alert" className="type-body-sm mt-10">
                  Could not cancel. Please try again.
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
